import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { dateInputToISO, isoToDateInput } from "@/lib/dateFormat";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * "Gespräch festhalten" dialog.
 * Reihenfolge zwingend (NORA Spec):
 *   1. Eindruck
 *   2. Vereinbarungen
 *   3. Nächster Schritt
 *   4. Wiedervorlage
 * Person + Art + Datum sind Metadaten oben; freie Notiz unten.
 */
export default function ConversationLogDialog({
  open,
  onOpenChange,
  contactId,
  contactName,
  challenges = [],
  onSaved,
}) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(defaultForm(contactId));

  useEffect(() => {
    if (open) {
      setForm(defaultForm(contactId));
    }
  }, [open, contactId]);

  // If no contactId provided (opened from Challenges page), we need a contact picker
  const contactPickerNeeded = !contactId;
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts", "all-for-picker"],
    queryFn: async () => (await api.get(`/contacts`, { params: { include_red: "true" } })).data,
    enabled: open && contactPickerNeeded,
  });

  const setField = (name, value) => setForm((s) => ({ ...s, [name]: value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.contact_id) {
      toast.error("Bitte eine Person auswählen.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contact_id: form.contact_id,
        challenge_id: form.challenge_id || null,
        datum: form.datum ? dateInputToISO(form.datum) : new Date().toISOString(),
        art: form.art || "persoenlich",
        eindruck: form.eindruck || null,
        vereinbarungen: form.vereinbarungen || null,
        naechster_schritt: form.naechster_schritt || null,
        wiedervorlage: form.wiedervorlage ? dateInputToISO(form.wiedervorlage) : null,
        freie_notiz: form.freie_notiz || null,
      };
      await api.post(`/conversation-logs`, payload);
      toast.success("Gespräch gespeichert");
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["conversation-logs"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["followups"] });
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="conv-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Gespräch erfassen</DialogTitle>
          <DialogDescription>
            {contactName ? `Mit ${contactName}.` : "Person auswählen und Gespräch dokumentieren."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Metadaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contactPickerNeeded ? (
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Person <span className="text-rose-500">*</span>
                </Label>
                <Select value={form.contact_id || ""} onValueChange={(v) => setField("contact_id", v)}>
                  <SelectTrigger className="mt-1" data-testid="conv-log-field-contact_id">
                    <SelectValue placeholder="Person auswählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                        {c.company_name ? ` – ${c.company_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Datum</Label>
              <Input
                type="date"
                value={form.datum}
                onChange={(e) => setField("datum", e.target.value)}
                className="mt-1"
                data-testid="conv-log-field-datum"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Art</Label>
              <Select value={form.art} onValueChange={(v) => setField("art", v)}>
                <SelectTrigger className="mt-1" data-testid="conv-log-field-art">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="persoenlich">Persönlich</SelectItem>
                  <SelectItem value="telefon">Telefon</SelectItem>
                  <SelectItem value="email">E-Mail</SelectItem>
                  <SelectItem value="nachricht">Nachricht</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                Herausforderung (optional)
              </Label>
              <Select
                value={form.challenge_id || "__none__"}
                onValueChange={(v) => setField("challenge_id", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="conv-log-field-challenge_id">
                  <SelectValue placeholder="Keine Herausforderung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Keine Herausforderung —</SelectItem>
                  {challenges.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      {ch.titel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Genau vorgegebene Reihenfolge */}
          <NumberedStep n={1} title="Was war wichtig?">
            <Textarea
              value={form.eindruck}
              onChange={(e) => setField("eindruck", e.target.value)}
              rows={3}
              placeholder="Was war das Wichtigste an diesem Gespräch?"
              data-testid="conv-log-field-eindruck"
            />
          </NumberedStep>

          <NumberedStep n={2} title="Was wurde vereinbart?">
            <Textarea
              value={form.vereinbarungen}
              onChange={(e) => setField("vereinbarungen", e.target.value)}
              rows={2}
              placeholder="Konkrete Absprachen, Zusagen, Termine."
              data-testid="conv-log-field-vereinbarungen"
            />
          </NumberedStep>

          <NumberedStep n={3} title="Was mache ich als Nächstes?">
            <Textarea
              value={form.naechster_schritt}
              onChange={(e) => setField("naechster_schritt", e.target.value)}
              rows={2}
              placeholder="Nächster konkreter Schritt (wird automatisch als Wiedervorlage angelegt)."
              data-testid="conv-log-field-naechster_schritt"
            />
          </NumberedStep>

          <NumberedStep n={4} title="Wiedervorlage">
            <Input
              type="date"
              value={form.wiedervorlage}
              onChange={(e) => setField("wiedervorlage", e.target.value)}
              data-testid="conv-log-field-wiedervorlage"
            />
            <p className="text-xs text-gray-500 mt-1">
              Nur ein Datum – wird automatisch als Wiedervorlage mit dem nächsten Schritt angelegt.
            </p>
          </NumberedStep>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Eigene Ergänzung</Label>
            <Textarea
              value={form.freie_notiz}
              onChange={(e) => setField("freie_notiz", e.target.value)}
              rows={2}
              placeholder="Weitere Details, die nicht in die Zusammenfassung sollen."
              className="mt-1"
              data-testid="conv-log-field-freie_notiz"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="conv-log-cancel-btn">
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={submitting}
              data-testid="conv-log-submit-btn"
            >
              {submitting ? "Speichern…" : "Gespräch speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NumberedStep({ n, title, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center font-semibold">
          {n}
        </span>
        <Label className="text-sm font-semibold text-gray-900">{title}</Label>
      </div>
      {children}
    </div>
  );
}

function toLocalInput(d) {
  // Return YYYY-MM-DD for <input type="date">
  return isoToDateInput(d || new Date());
}

function defaultForm(contactId) {
  return {
    contact_id: contactId || "",
    challenge_id: "",
    datum: toLocalInput(new Date()),
    art: "persoenlich",
    eindruck: "",
    vereinbarungen: "",
    naechster_schritt: "",
    wiedervorlage: "",
    freie_notiz: "",
  };
}
